// prune-orphan-tool-hooks.ts
//
// Khi pack (qua `avatar sync`) BỎ một tool khỏi .claude/pack/tools/, các hook-entry
// tool đó từng ghi vào .claude/settings.json bị bỏ lại MỒ CÔI (orphan) — trỏ tới
// script pack/tools/<tool>/hooks/*.py đã bị xoá → Claude Code cố chạy script mất
// tích mỗi lượt. Module này phát hiện orphan tool + prune các hook-entry của chúng
// khỏi settings.json (KHÔNG đụng hook user / tool còn sống / core Avatar).
//
// Orphan = enabled-in-state (avatar-tools.json) NHƯNG không còn trong pack
// (listAvailableTools() không trả về). Manifest orphan đã mất → KHÔNG dùng được
// disableTool(manifest); nhận diện bằng SCOPED PATH-PREFIX (HD1-B):
//   command chứa `/.claude/pack/tools/<orphan>/` → thuộc orphan → xoá.
// Trailing slash chặn name-collision (orphan `plan` KHÔNG match `plan-monitor`).
// Match = ENTRY-LEVEL (per-event), không group-level (tránh bug dedupe cũ).
//
// Purity: module này CHỈ prune settings.json. State flip (enabled=false) sống ở
// sync tail (P02) — đọc `status` để quyết định flip / defer (dễ unit test, SRP).
//
// Fail-soft: settings thiếu → no-op; settings malformed → settings-unreadable
// (KHÔNG throw); pack rỗng bất thường (bad/partial tarball, available=[] khi còn
// tool enabled) → skipped-anomalous-pack (chặn mass-wipe). Reuse backup + atomic
// write + backupFilename có sẵn (DRY).

import { promises as fs } from "node:fs";
import { join } from "node:path";
import { listAvailableTools } from "./discover-pack-tools-and-defaults.js";
import { pathExists, readJson, writeJsonAtomic } from "./filesystem-helpers.js";
import {
  backupFilename,
  type PackSettingsTemplate,
} from "./merge-pack-settings-into-project-settings.js";
import { listEnabledTools } from "./tool-state-store.js";
import { withSettingsFileLock } from "./with-settings-file-lock.js";
import { SECRET_FILE_MODE } from "./write-claude-settings-json-per-project.js";

// Một hook entry trong settings: { matcher?, hooks: [{type, command}] }.
interface HookEntry {
  matcher?: string;
  hooks?: Array<{ type?: string; command?: string }>;
}

// status gate cho P02: flip enabled=false khi `pruned`|`no-op`; DEFER khi
// `settings-unreadable`; SKIP khi `skipped-anomalous-pack` (orphans rỗng sẵn).
export type PruneStatus = "pruned" | "no-op" | "settings-unreadable" | "skipped-anomalous-pack";

export interface PruneOrphanResult {
  status: PruneStatus;
  orphans: string[];
  removedByTool: Record<string, number>;
  changes: string[];
  backupPath?: string;
  settingsMissing?: boolean; // true chỉ khi settings.json vắng (subset của no-op)
  warning?: string;
}

// Prefix nhận diện hook thuộc tool. Leading + trailing slash: settings lưu literal
// ${CLAUDE_PROJECT_DIR}/.claude/pack/tools/<t>/... (forward slash, cross-platform)
// → forward-slash prefix match. Trailing slash chặn `plan` khớp `plan-monitor`.
export function packToolHookPrefix(tool: string): string {
  return `/.claude/pack/tools/${tool}/`;
}

// Trả tên orphan sở hữu command (nếu có), else null.
export function commandOwnedByOrphan(command: string, orphans: string[]): string | null {
  for (const t of orphans) {
    if (command.includes(packToolHookPrefix(t))) return t;
  }
  return null;
}

// Lọc command orphan khỏi 1 entry Ở CẤP COMMAND (không xoá cả entry chỉ vì 1
// command trỏ orphan) → giữ được hook user hand-edit lỡ chèn chung block với hook
// orphan (AC2). Trả: entry đã lọc (giữ matcher), null nếu entry RỖNG hoàn toàn sau
// lọc, hoặc entry gốc nếu không command nào bị bỏ. Đếm mỗi command bỏ theo đúng owner.
function pruneOrphanCommandsFromEntry(
  entry: HookEntry,
  orphans: string[],
  removedByTool: Record<string, number>,
): HookEntry | null {
  const hooks = entry.hooks ?? [];
  const keptHooks = hooks.filter((h) => {
    const owner = commandOwnedByOrphan(h.command ?? "", orphans);
    if (owner) {
      removedByTool[owner] = (removedByTool[owner] ?? 0) + 1;
      return false;
    }
    return true;
  });
  if (keptHooks.length === hooks.length) return entry; // không bỏ gì → giữ nguyên
  if (keptHooks.length === 0) return null; // rỗng hoàn toàn → bỏ entry
  return { ...entry, hooks: keptHooks }; // 1 phần → giữ matcher + command sống sót
}

function projectSettingsPath(workspacePath: string): string {
  return join(workspacePath, ".claude", "settings.json");
}

export async function pruneOrphanToolHooks(workspacePath: string): Promise<PruneOrphanResult> {
  const [available, enabled] = await Promise.all([
    listAvailableTools(workspacePath),
    listEnabledTools(workspacePath),
  ]);

  // B1/R1 DEGENERATE-PACK GUARD: bad/partial tarball → .claude/pack/tools/ thiếu/rỗng
  // → listAvailableTools() trả [] trong khi vẫn còn tool enabled → naive orphan-set =
  // MỌI tool enabled → prune sẽ xoá sạch hook + P02 flip tất cả enabled=false. Chặn:
  // available=[] && enabled>0 → skip hoàn toàn (KHÔNG đọc settings, KHÔNG xoá, KHÔNG
  // flip). Anomaly tự khỏi ở sync tốt kế tiếp (available có lại → 0 orphan).
  if (available.length === 0 && enabled.length > 0) {
    return {
      status: "skipped-anomalous-pack",
      orphans: [],
      removedByTool: {},
      changes: [],
      warning: `pack lists 0 available tools while ${enabled.length} enabled — skipping prune to avoid mass-wipe`,
    };
  }

  const orphans = enabled.filter((t) => !available.includes(t));
  if (orphans.length === 0) {
    return { status: "no-op", orphans: [], removedByTool: {}, changes: [] };
  }

  // #164 writer #4: read (:130) + write (:175) trong CÙNG bracket. Hai bước tính
  // orphan ở trên chỉ đọc pack + state (không đua với settings.json) nên để ngoài
  // lock — giữ critical section hẹp nhất có thể (AC5).
  return await withSettingsFileLock(workspacePath, () =>
    pruneOrphanHooksUnderLock(workspacePath, orphans),
  );
}

// Thân cũ (từ chỗ đọc settings.json tới chỗ ghi) — dịch nguyên văn, KHÔNG đổi logic.
async function pruneOrphanHooksUnderLock(
  workspacePath: string,
  orphans: string[],
): Promise<PruneOrphanResult> {
  const settingsPath = projectSettingsPath(workspacePath);
  // settings vắng → không có hook để prune → no-op vô hại (flip-safe cho P02).
  if (!(await pathExists(settingsPath))) {
    return { status: "no-op", orphans, removedByTool: {}, changes: [], settingsMissing: true };
  }

  let settings: PackSettingsTemplate;
  try {
    settings = await readJson<PackSettingsTemplate>(settingsPath);
  } catch (err) {
    // B2/AC5: settings malformed → hook KHÔNG đọc được → KHÔNG throw, KHÔNG ghi.
    // status=settings-unreadable để P02 DEFER flip (orphan vẫn detect được, sync
    // sau thử lại khi settings parse lại).
    return {
      status: "settings-unreadable",
      orphans,
      removedByTool: {},
      changes: [],
      warning: `settings.json không parse được: ${err instanceof Error ? err.message : String(err)} — bỏ qua prune, sync sau sẽ thử lại`,
    };
  }

  // Entry-level per-event filter: xoá entry thuộc orphan, giữ entry khác. Dọn
  // mảng event rỗng (omit key), không để lại `[]`.
  const userHooks = (settings.hooks ?? {}) as Record<string, HookEntry[]>;
  const outHooks: Record<string, HookEntry[]> = {};
  const removedByTool: Record<string, number> = {};
  for (const [event, entries] of Object.entries(userHooks)) {
    const kept: HookEntry[] = [];
    for (const entry of entries) {
      const pruned = pruneOrphanCommandsFromEntry(entry, orphans, removedByTool);
      if (pruned !== null) kept.push(pruned);
    }
    if (kept.length > 0) outHooks[event] = kept;
  }
  const totalRemoved = Object.values(removedByTool).reduce((sum, n) => sum + n, 0);

  // Orphan có trong state nhưng KHÔNG có hook nào trong settings → no-op (không
  // ghi). P02 vẫn flip enabled=false (state khớp thực tế: tool đã rời pack).
  if (totalRemoved === 0) {
    return { status: "no-op", orphans, removedByTool: {}, changes: [] };
  }

  // Tái dựng settings, OMIT key hooks nếu rỗng (noDelete convention, không `delete`).
  const { hooks: _h, ...rest } = settings;
  const merged: PackSettingsTemplate = { ...rest };
  if (Object.keys(outHooks).length > 0) {
    merged.hooks = outHooks as Record<string, unknown[]>;
  }

  // Backup + atomic write CHỈ khi có thay đổi (reuse primitives, không corrupt partial).
  const backupPath = backupFilename(settingsPath);
  await fs.copyFile(settingsPath, backupPath);
  // #164 chốt (f): giữ mode 0600 — file này chứa API key của member.
  await writeJsonAtomic(settingsPath, merged, SECRET_FILE_MODE);

  const changes: string[] = [];
  for (const t of orphans) {
    const n = removedByTool[t] ?? 0;
    if (n > 0) changes.push(`pruned ${n} orphan hook(s) from dropped tool '${t}'`);
  }

  return { status: "pruned", orphans, removedByTool, changes, backupPath };
}
